/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { ReactElement, useMemo, useRef, useState } from 'react';
import { BleChannelCell } from '../../services/ble-admin.service';

type Props = {
  cells: BleChannelCell[];
  channelCount: number;
  from: number;
  to: number;
  bucketMs: number;
  min: number | null;
  max: number | null;
  /** Shown in the axis label, e.g. "channel_data". */
  parameterKey: string;
  loading?: boolean;
};

/**
 * Perceptually-ordered ramp (dark blue -> cyan -> yellow), sampled from the
 * viridis family. Chosen over a rainbow because it is monotonic in lightness,
 * so a reader sees the ordering even where the hues are hard to tell apart,
 * and it stays legible in greyscale and for colour-blind viewers.
 */
const RAMP = [
  '#0d1b3e',
  '#20356b',
  '#2a5090',
  '#2b6ca8',
  '#2688b0',
  '#2fa3a8',
  '#5cbd91',
  '#9ed271',
  '#d7e159',
  '#fde725'
];

/** Cell colour for a value, or null for "no data" (which is NOT zero). */
function colorFor(value: number | undefined, lo: number, hi: number): string | null {
  if (value === undefined || !Number.isFinite(value)) return null;
  if (hi <= lo) return RAMP[0];
  // sqrt rather than linear: channel counts are heavily skewed towards the
  // smallest size channel, so a linear ramp leaves every other channel the
  // same dark blue and the plot says nothing.
  const norm = Math.sqrt(Math.max(0, Math.min(1, (value - lo) / (hi - lo))));
  return RAMP[Math.min(RAMP.length - 1, Math.floor(norm * RAMP.length))];
}

function fmtValue(v: number): string {
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

function fmtTime(ms: number, spanMs: number): string {
  const d = new Date(ms);
  // Over a day, the clock time alone is ambiguous.
  return spanMs > 86400000
    ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/**
 * Size-channel heat map: channel index up the y-axis, time along the x-axis,
 * colour by concentration — the same reading as the OPC-N3 bin contour plot,
 * for the array the Aria sends on every ping.
 *
 * Drawn as absolutely-positioned divs inside one relative box rather than with
 * a chart library: the data is already a dense regular grid, so there is no
 * axis maths to do, and a few thousand divs render faster than they would
 * round-trip through a charting runtime. It also keeps the gap handling exact —
 * a bucket with no pings has no div at all, so it shows the background rather
 * than a zero.
 */
export default function ChannelHeatMap({
  cells,
  channelCount,
  from,
  to,
  bucketMs,
  min,
  max,
  parameterKey,
  loading = false
}: Props): ReactElement {
  const [hover, setHover] = useState<{ cell: BleChannelCell; x: number; y: number } | null>(null);
  const box = useRef<HTMLDivElement | null>(null);

  const grid = useMemo(() => {
    const columns = Math.max(1, Math.ceil((to - from) / bucketMs));
    // Index by bucket so lookup is O(1) per cell and the column position is
    // derived from the timestamp, never from the row's order in the response.
    const byKey = new Map<string, BleChannelCell>();
    cells.forEach((c) => byKey.set(`${Math.floor((c.t - from) / bucketMs)}:${c.channel}`, c));
    return { columns, byKey };
  }, [cells, from, to, bucketMs]);

  const lo = min ?? 0;
  const hi = max ?? 0;
  const rows = Math.max(1, channelCount);
  const colWidth = 100 / grid.columns;
  const rowHeight = 100 / rows;
  const spanMs = to - from;

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-gray-800 bg-container-500 p-4">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-100">Size channels</h3>
        <span className="text-xs text-gray-500">
          {parameterKey} · {rows} channels · {cells.length.toLocaleString()} cells
        </span>
      </div>

      <div className="flex min-w-0 flex-1">
        {/* y axis: channel 0 at the bottom, like a size histogram. */}
        <div className="mr-2 flex w-8 shrink-0 flex-col justify-between py-0.5 text-right text-[10px] text-gray-500">
          {Array.from({ length: rows }, (_, i) => (
            <span key={rows - 1 - i} className="leading-none">
              ch{rows - 1 - i}
            </span>
          ))}
        </div>

        <div className="relative min-w-0 flex-1">
          <div
            ref={box}
            className="relative h-56 w-full overflow-hidden rounded border border-gray-800 bg-background-700"
            onMouseLeave={() => setHover(null)}
          >
            {cells.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                {loading ? 'Loading channel data…' : 'No channel data in this range'}
              </div>
            )}
            {cells.map((c) => {
              const column = Math.floor((c.t - from) / bucketMs);
              if (column < 0 || column >= grid.columns) return null;
              const color = colorFor(c.value, lo, hi);
              if (color === null) return null;
              return (
                <div
                  key={`${c.t}:${c.channel}`}
                  role="presentation"
                  onMouseEnter={(e) => {
                    const bounds = box.current?.getBoundingClientRect();
                    setHover({
                      cell: c,
                      x: bounds ? e.clientX - bounds.left : 0,
                      y: bounds ? e.clientY - bounds.top : 0
                    });
                  }}
                  style={{
                    position: 'absolute',
                    left: `${column * colWidth}%`,
                    // Channel 0 at the bottom.
                    bottom: `${c.channel * rowHeight}%`,
                    // The +0.6% overlap hides sub-pixel seams between columns,
                    // which otherwise show as a grid of hairlines over the plot.
                    width: `${colWidth + 0.6}%`,
                    height: `${rowHeight}%`,
                    backgroundColor: color
                  }}
                />
              );
            })}

            {hover && (
              <div
                className="pointer-events-none absolute z-10 rounded border border-gray-700 bg-background-500/95 px-2 py-1 text-[11px] text-gray-100 shadow-lg"
                style={{
                  left: Math.min(hover.x + 8, 999),
                  top: Math.max(hover.y - 46, 2),
                  // Keeps the tooltip inside the plot near the right edge.
                  transform: hover.x > 240 ? 'translateX(-100%)' : undefined
                }}
              >
                <div className="font-semibold">
                  ch{hover.cell.channel} · {fmtValue(hover.cell.value)}
                </div>
                <div className="text-gray-400">
                  {fmtTime(hover.cell.t, spanMs)} · n {hover.cell.n}
                </div>
              </div>
            )}
          </div>

          <div className="mt-1 flex justify-between text-[10px] text-gray-500">
            <span>{fmtTime(from, spanMs)}</span>
            <span>{fmtTime(to, spanMs)}</span>
          </div>
        </div>
      </div>

      {/* Colour scale. Labelled with the real extent so the plot is readable
          as magnitudes, not just as a pattern. */}
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[10px] text-gray-500">{fmtValue(lo)}</span>
        <div className="flex h-2 flex-1 overflow-hidden rounded">
          {RAMP.map((c) => (
            <div key={c} className="flex-1" style={{ backgroundColor: c }} />
          ))}
        </div>
        <span className="text-[10px] text-gray-500">{fmtValue(hi)}</span>
        <span className="ml-1 text-[10px] text-gray-600">counts (√ scale)</span>
      </div>
    </div>
  );
}
